'use client';

import { FC } from 'react';
import { Navigation } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';

import { Button } from '@/components/ui/button';
import { hero } from '@/data';
import { TextGenerateEffect } from '../ui/text-generate-effect';

// TextGenerateEffect runs stagger(0.2) with duration 0.5, so the headline settles at
// (words - 1) * 0.2 + 0.5. Derived from the copy so a rewrite cannot desync the sequence.
const TITLE_SETTLE = Math.min((hero.title.trim().split(/\s+/).length - 1) * 0.2 + 0.5, 1.2);

export const Title: FC = () => {
  const reduced = useReducedMotion();

  const rise = (delay: number) => ({
    initial: { opacity: 0, y: reduced ? 0 : 12 },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: reduced ? 0.2 : 0.6,
      delay: reduced ? 0 : Math.max(delay, 0),
      ease: [0.22, 1, 0.36, 1] as const,
    },
  });

  return (
    <div className="flex justify-center relative my-20 z-10">
      <div className="max-w-[90vw] md:max-w-2xl lg:max-w-[60vw] flex flex-col items-center">
        <motion.p
          {...rise(0)}
          className="uppercase tracking-widest text-xs text-center text-blue-100 max-w-[80vw]"
        >
          {hero.eyebrow}
        </motion.p>

        {/* highlightFormIndex is the index of the LAST white word: purple starts at 'from'. */}
        <TextGenerateEffect
          className="text-center text-4xl md:text-5xl lg:text-6xl"
          words={hero.title}
          highlightFormIndex={3}
        />

        <motion.p
          {...rise(TITLE_SETTLE - 0.35)}
          className="text-center md:tracking-wider mb-4 text-sm md:text-lg lg:text-2xl"
        >
          {hero.description}
        </motion.p>

        <motion.div {...rise(TITLE_SETTLE - 0.15)} className="w-full flex justify-center">
          <a href="#projects" className="w-full md:w-auto">
            <Button variant="magic">
              View projects
              <Navigation className="size-4 ml-2" />
            </Button>
          </a>
        </motion.div>
      </div>
    </div>
  );
};
